// src/components/PrivacyPolicy/PrivacyPolicy.jsx
import PrivacyPolicyHelmetComponent from "./PrivacyPolicyHelmetComponent";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const pageShellClass =
  "relative w-full overflow-hidden bg-gradient-to-br from-page via-surface2 to-surface3 px-4 py-24 text-[#111827] dark:from-darkpage dark:via-darksurface dark:to-darksurface2 dark:text-white sm:px-6 lg:px-8";

const contentCardClass =
  "relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-8 lg:p-10";

const badgeClass =
  "mb-5 inline-flex rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase tracking-[0.14em] !text-[#111827] shadow-sm dark:!text-white";

const titleClass =
  "font-customFont2 text-5xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-6xl lg:text-7xl";

const sectionTitleClass =
  "font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-4xl";

const textClass =
  "mt-4 font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl [&_strong]:font-extrabold [&_strong]:!text-[#111827] dark:[&_strong]:!text-white";

const sectionClass =
  "rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-5 shadow-sm dark:border-darkborder dark:bg-darksurface/80 sm:p-6";

const linkClass =
  "font-extrabold !text-[#111827] underline decoration-primary decoration-2 underline-offset-4 transition hover:!text-primary dark:!text-white dark:hover:!text-primary";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <main className={pageShellClass}>
      <PrivacyPolicyHelmetComponent />

      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute left-1/2 top-16 h-72 w-72
          -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
          dark:bg-primary/10
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute -right-24 top-96 h-96 w-96
          rounded-full bg-[#30f2cf]/10 blur-3xl
          dark:bg-primary/10
        "
      />

      <section className={contentCardClass} aria-labelledby="privacy-title">
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute -right-20 -top-20 h-56 w-56
            rounded-full bg-primary/10 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative">
          <ResponsiveButton
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate("/");
              }

              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }, 100);
            }}
            variant="backbutton"
            className="mb-8"
          >
            <div className="flex items-center gap-2">
              <MdArrowBack className="text-xl" />
              <span>Takaisin</span>
            </div>
          </ResponsiveButton>

          <span className={badgeClass}>Tietosuoja</span>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div>
              <h1 id="privacy-title" className={titleClass}>
                Spiderlegs Productions tietosuojalausunto
              </h1>

              <p className={`${textClass} max-w-4xl`}>
                Tässä tietosuojalausunnossa kuvataan, miten Spiderlegs
                Productions käsittelee verkkosivuston ja varauspalveluiden
                yhteydessä annettuja henkilötietoja.
              </p>
            </div>

            <aside
              className="
                rounded-3xl border border-primary/30 bg-primary/10 p-5
                dark:border-primary/30 dark:bg-primary/5
              "
            >
              <p className="font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white">
                Rekisterinpitäjä
              </p>

              <p className="mt-3 font-customFont1 text-base font-bold leading-relaxed !text-[#111827] dark:!text-white">
                Spiderlegs Productions
                <br />
                Y-tunnus 3214378-6
                <br />
                Kerava, Suomi
              </p>
            </aside>
          </div>

          <div className="mt-10 space-y-6">
            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>1. Johdanto</h2>

              <p className={textClass}>
                Spiderlegs Productions Oy, Y-tunnus 3214378-6, kunnioittaa
                asiakkaidensa yksityisyyttä ja on sitoutunut noudattamaan EU:n
                yleistä tietosuoja-asetusta, GDPR, EU 2016/679, sekä muuta
                soveltuvaa tietosuojalainsäädäntöä. Tämä tietosuojalausunto
                kuvaa, miten keräämme, käytämme ja käsittelemme henkilötietoja
                verkkosivustollamme ja sen kautta.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>2. Henkilötietojen kerääminen</h2>

              <p className={textClass}>
                Keräämme verkkosivustollamme olevien yhteydenottolomakkeiden,
                varauslomakkeiden ja vuokrauspalvelun kautta saapuneita
                tietoja. Keräämämme tiedot voivat olla:
              </p>

              <ul className="mt-4 list-disc space-y-2 pl-6 font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl">
                <li>
                  <strong>Nimi:</strong> varaajan nimi.
                </li>
                <li>
                  <strong>Sähköpostiosoite:</strong> varaajan sähköpostiosoite.
                </li>
                <li>
                  <strong>Puhelinnumero:</strong> varaajan puhelinnumero.
                </li>
                <li>
                  <strong>Tapahtumapaikka:</strong> tapahtuman järjestämispaikka.
                </li>
                <li>
                  <strong>Aloituspäivämäärä:</strong> varauksen aloituspäivä.
                </li>
                <li>
                  <strong>Lopetuspäivämäärä:</strong> varauksen lopetuspäivä.
                </li>
                <li>
                  <strong>Rekisterinumero:</strong> mahdollisesti peräkärryn
                  vuokrauksen yhteydessä.
                </li>
                <li>
                  <strong>Viesti:</strong> varaajan jättämä viesti tai
                  lisätiedot.
                </li>
                <li>
                  <strong>Tietojen tallennuksen ajankohta:</strong> tietojen
                  tallentamisen ajankohta.
                </li>
              </ul>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>3. Henkilötietojen käyttö</h2>

              <p className={textClass}>
                Käytämme lomakkeiden ja varauspalvelun kautta tulleita tietoja
                ainoastaan palvelun toteuttamiseen, yhteydenottoihin ja
                asiakassuhteen hoitamiseen.
              </p>

              <ul className="mt-4 list-disc space-y-2 pl-6 font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl">
                <li>Vastataksemme kysymyksiin ja yhteydenottopyyntöihin.</li>
                <li>Ylläpitääksemme asiakassuhdetta.</li>
                <li>Käsitelläksemme varauksia ja vuokrauspyyntöjä.</li>
                <li>Tarjotaksemme asiakkaalle sovittuja palveluita ja tarjouksia.</li>
              </ul>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>4. Henkilötietojen jakaminen</h2>

              <p className={textClass}>
                Emme jaa henkilötietoja kolmansille osapuolille ilman
                suostumusta, paitsi jos laki niin määrää tai jos tietojen
                käsittely on tarpeen palveluidemme tarjoamiseksi,
                asiakasviestinnän hoitamiseksi tai lakisääteisten velvoitteiden
                täyttämiseksi.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>5. Henkilötietojen säilytys</h2>

              <p className={textClass}>
                Säilytämme henkilötietoja vain niin kauan kuin on tarpeen
                edellä mainittujen käyttötarkoitusten täyttämiseksi tai lain
                edellyttämien määräaikojen mukaan.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>6. Tietoturva</h2>

              <p className={textClass}>
                Käytämme asianmukaisia teknisiä ja organisatorisia toimenpiteitä
                suojataksemme henkilötietoja luvattomalta käsittelyltä,
                käytöltä, paljastamiselta, muuttamiselta tai tuhoamiselta.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>7. Oikeutesi</h2>

              <p className={textClass}>Sinulla on oikeus:</p>

              <ul className="mt-4 list-disc space-y-2 pl-6 font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl">
                <li>saada tietoa siitä, miten käsittelemme henkilötietojasi,</li>
                <li>pyytää henkilötietojesi oikaisemista tai poistamista,</li>
                <li>rajoittaa henkilötietojesi käsittelyä,</li>
                <li>vastustaa henkilötietojesi käsittelyä.</li>
              </ul>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>8. Yhteystiedot</h2>

              <p className={textClass}>
                Jos sinulla on kysyttävää henkilötietojesi käsittelystä tai
                haluat käyttää oikeuksiasi, ota yhteyttä:
              </p>

              <div
                className="
                  mt-5 rounded-3xl border border-primary/30 bg-primary/10 p-5
                  font-customFont1 text-lg font-bold leading-relaxed
                  !text-[#111827]
                  dark:border-primary/30 dark:bg-primary/5 dark:!text-white
                "
              >
                Spiderlegs Productions
                <br />
                Arttu Stenius
                <br />
                +358 44 351 92 65
                <br />
                Sähköposti:{" "}
                <a
                  href="mailto:info@spiderlegsproductions.com"
                  title="Lähetä sähköpostia"
                  className={linkClass}
                >
                  info@spiderlegsproductions.com
                </a>
              </div>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>9. Muutokset</h2>

              <p className={textClass}>
                Tätä tietosuojalausuntoa voidaan päivittää ajoittain.
                Ilmoitamme merkittävistä muutoksista verkkosivustollamme.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>10. Lisätietoja tietosuojasta</h2>

              <p className={textClass}>
                Lisätietoja tietosuojalainsäädännöstä saat
                tietosuojavaltuutetun verkkosivuilta:
              </p>

              <p className="mt-4 font-customFont1 text-lg font-bold sm:text-xl">
                <a
                  href="https://www.tietosuoja.fi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Avaa tietosuojavaltuutetun verkkosivut"
                  className={linkClass}
                >
                  https://www.tietosuoja.fi/
                </a>
              </p>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;